import React from 'react';
import cx from 'classnames';
import { Formik } from 'formik';
import * as yup from 'yup';

import { Form } from '@magoosh/lib/forms';
import { PrimaryButton } from '@magoosh/lib/button';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';

import { validateUserData } from '@payments/api';
import ScrollToError from '@payments/components/scroll_to_error';
import EuTerms from '@payments/components/terms/eu';
import UsTerms from '@payments/components/terms/us';
import UserFormGroup from '@payments/components/form_groups/user';

import style from '@payments/style.module.scss';

// A user form view for 2-step signup. This form is submitted so that the credentials can be
// validated without creating an account, for the purpose of sending cart-abandon emails. After
// this form is successfully submitted, the purchase view is shown.

interface Props {
  isEuUser: boolean;
  onComplete: (any) => void;
}

interface UserFormValues {
  email: string;
  password: string;
  termsAndPolicyAccepted: boolean;
}

const UserForm: React.FC<Props> = (props) => {
  const authenticityToken = useAuthenticityToken();

  const validationSchema = yup.object({
    email: yup.string().label('Email Address').required(),
    password: yup.string().label('Password').required(),
    passwordConfirmation: yup.string().label('Password Confirmation').required(),
    termsAndPolicyAccepted: yup.bool().oneOf([true], 'Terms of Use and Privacy Policy must be accepted')
  });

  const initialValues = {
    email: '',
    password: '',
    passwordConfirmation: '',
    termsAndPolicyAccepted: !props.isEuUser
  };

  const handleOnSubmit = (values: UserFormValues, { setSubmitting, setErrors }) => {
    setSubmitting(true);

    validateUserData(values, authenticityToken)
      .then((response) => {
      props.onComplete(values);
    }).catch((response) => {
      setErrors(response.errors.user);
      setSubmitting(false);
    });
  };

  const submitButtonName = 'Continue to Payment';

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      {({
        handleSubmit,
        isSubmitting,
        values
      }) => (
        <Form onSubmit={handleSubmit}>
          <>
            <ScrollToError />
            <UserFormGroup />

            {props.isEuUser && <EuTerms buttonName={submitButtonName} />}
            <PrimaryButton
              className={cx('u-margin-B-m', style.btnBlockOnMobile)}
              onClick={handleSubmit}
              type="submit"
              submitting={isSubmitting}
            >
              {submitButtonName}
            </PrimaryButton>
            {!props.isEuUser && <UsTerms buttonName={submitButtonName} />}
          </>
        </Form>
      )}
    </Formik>
  );
};

export default UserForm;
